// src/pages/languages/LanguageSkillsList.jsx
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Plus,
  BookOpen,
  Edit2,
  Trash2,
  Volume2,
  PenTool,
  FileText,
  Headphones,
  Mic,
  Layers,
  ArrowRight,
  RefreshCw,CreditCard
} from "lucide-react";
import {
  languageSkillsAPI,
  getApiError,
} from "../../services/languageService";
import MergeMixedModal from "./MergeMixedModal";

const skillTypeConfig = {
  VOCABULARY: {
    label: "Vocabulary",
    icon: Volume2,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  GRAMMAR: {
    label: "Grammar",
    icon: PenTool,
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
  READING: {
    label: "Reading",
    icon: BookOpen,
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
  LISTENING: {
    label: "Listening",
    icon: Headphones,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
  },
  SPEAKING: {
    label: "Speaking",
    icon: Mic,
    color: "text-pink-600",
    bg: "bg-pink-50",
  },
  WRITING: {
    label: "Writing",
    icon: FileText,
    color: "text-green-600",
    bg: "bg-green-50",
  },
  MIXED: {
    label: "Mixed",
    icon: Layers,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  GENERAL_PATH: {
    label: "General Path",
    icon: Layers,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
};

// بيطلع الـ id بتاع اللغة من أي شكل ممكن يرجع من الـ API
const extractLanguageId = (cat) => {
  if (!cat) return null;
  if (cat.language_id) return cat.language_id;
  if (cat.language && typeof cat.language === "object") return cat.language.id ?? null;
  if (cat.language) return cat.language;
  return null;
};

// GENERAL_PATH هو الاسم القديم لـ MIXED
const isMixed = (skill) =>
  skill.skill_type === "MIXED" || skill.skill_type === "GENERAL_PATH";

export default function LanguageSkillsList() {
  const { categoryId } = useParams();
  const [skills, setSkills] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [languageId, setLanguageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mergeModal, setMergeModal] = useState(null); // { target: skill | null }
  const [notice, setNotice] = useState("");

  useEffect(() => {
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const fetchSkills = async () => {
  try {
    setLoading(true);
    const data = await languageSkillsAPI.getAll(categoryId);
    setSkills(data.skills || []);
    setCategoryInfo(data.category || null);
    setLanguageId(extractLanguageId(data.category));
  } catch (e) {
    console.error(e);
  } finally {
    setLoading(false);
  }
};

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه المهارة؟")) return;
    try {
      await languageSkillsAPI.delete(id);
      setSkills((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(getApiError(err, "حدث خطأ أثناء الحذف"));
    }
  };

  const handleMergeDone = (data) => {
    setMergeModal(null);
    setNotice(`${data.message} (تم نسخ ${data.total_copied} عنصر)`);
    fetchSkills();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // مهارات ميكسيد أصلية (مش المجمّعة نفسها)
  const mergeCandidates = skills.filter((s) => isMixed(s) && !s.is_merged);

  const backTo = languageId
    ? `/dashboard/languages/${languageId}/categories`
    : "/dashboard/languages";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link
            to={backTo}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowRight className="w-4 h-4" />
            رجوع
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            المهارات {categoryInfo?.name ? `— ${categoryInfo.name}` : ""}
          </h1>
          <p className="text-gray-500 text-sm mt-1">إدارة مهارات الكاتيجوري</p>
        </div>

        <div className="flex items-center gap-2">
          {mergeCandidates.length > 0 && (
            <button
              onClick={() => setMergeModal({ target: null })}
              className="flex items-center gap-2 border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              <Layers className="w-5 h-5" />
              <span>Merge Mixed</span>
            </button>
          )}
          <Link
            to={`/dashboard/languages/categories/${categoryId}/skills/create`}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة مهارة</span>
          </Link>
          <Link
            to={`/dashboard/languages/categories/${categoryId}/plans`}
            className="flex items-center gap-2 border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 px-4 py-2 rounded-lg transition-colors font-medium"
          >
            <CreditCard className="w-5 h-5" />
            <span>خطط الدفع</span>
          </Link>
        </div>
      </div>

      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button
            onClick={() => setNotice("")}
            className="font-medium underline"
          >
            إغلاق
          </button>
        </div>
      )}

      {skills.length === 0 ? (
        <div className="card text-center py-16">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">لا توجد مهارات</h3>
          <p className="text-gray-500 text-sm mb-6">ابدأ بإضافة مهارة جديدة</p>
          <Link
            to={`/dashboard/languages/categories/${categoryId}/skills/create`}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            إضافة مهارة جديدة
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {skills.map((skill) => {
            const config = skillTypeConfig[skill.skill_type] || {};
            const Icon = config.icon || BookOpen;
            return (
              <div
                key={skill.id}
                className="card hover:shadow-lg transition-shadow"
              >
                <div
                  className={`${config.bg} rounded-xl p-4 mb-4 flex items-center gap-3`}
                >
                  <Icon className={`w-8 h-8 ${config.color}`} />
                  <div>
                    <p className="font-bold text-gray-900">{skill.title}</p>
                    <p className={`text-xs font-medium ${config.color}`}>
                      {config.label}
                    </p>
                  </div>
                </div>

                {(skill.is_merged || !skill.is_active) && (
                  <div className="flex items-center gap-2 mb-3">
                    {skill.is_merged && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
                        Merged
                      </span>
                    )}
                    {!skill.is_active && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>
                )}

                {skill.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {skill.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-500">إجمالي الأسئلة</span>
                  <span className={`font-bold ${config.color}`}>
                    {skill.total_questions}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Link
                    to={`/dashboard/languages/skills/${skill.id}`}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition-colors"
                  >
                    إضافة / عرض الأسئلة
                  </Link>
                  <Link
                    to={`/dashboard/languages/skills/${skill.id}/edit`}
                    className="flex items-center justify-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDelete(skill.id)}
                    className="flex items-center justify-center p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {skill.is_merged && mergeCandidates.length > 0 && (
                  <button
                    onClick={() => setMergeModal({ target: skill })}
                    className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-xl py-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    تحديث المهارة المجمّعة
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mergeModal && (
        <MergeMixedModal
          categoryId={categoryId}
          candidates={mergeCandidates}
          target={mergeModal.target}
          onClose={() => setMergeModal(null)}
          onDone={handleMergeDone}
        />
      )}
    </div>
  );
}